import { v1WeightCascade } from '../../utils/v1WeightConversion';
/**
 * Lbs ↔ Kgs auto-calculation for the quote lane form (V1 new-quote parity:
 * typing either weight derives the other, e.g. 20040 kgs → 44180.2 lbs).
 *
 * Same UX semantics as the Pro drawer's weightCascade (OrderFormView.tsx) —
 * the edited field keeps the RAW typed text so reformatting never fights the
 * user mid-keystroke — and the SAME arithmetic: defs_quote.pl:265-266/375-376
 * wire the quote lane's lbs/kgs boxes to the very same `kgconvert("1"/"2")`,
 * so it shares the V1 module (#33560). Previously this divided by 2.2046 for
 * lbs->kgs; V1 multiplies by 0.4536, which is not the same number.
 *
 * No Nexa override — correctly so: V1's Nexa branch keys off an
 * `order_customercode` field, which the quote form does not render, so
 * `isNexa` is always 0 there. Metric tons likewise: the quote form has no MT
 * box (mtconvert bails when `prefix+"metrictons"` is undefined).
 */

export type QuoteWeightField = 'lbs' | 'kgs';
export type QuoteWeightPatch = Record<QuoteWeightField, string>;

/** Round to 1 decimal without trailing-zero noise ("44180.2", "1", not "1.0"). */
const fmt = (v: number): string => String(Math.round(v * 10) / 10);

/**
 * Given the field the user typed in and its raw text, return values for both
 * weight fields, or null when the entry isn't a number yet (a lone '-',
 * '1e', …) so the caller can stage the keystroke without cascading garbage.
 * Clearing one field clears the other too — a blank quote weight means blank
 * (the form's "—" placeholder), not zero.
 */
export function quoteWeightCascade(
  source: QuoteWeightField,
  raw: string,
): QuoteWeightPatch | null {
  // Users type big weights with separators ("44,180.2"); Number() would NaN.
  const trimmed = raw.trim().replace(/,/g, '');
  if (trimmed === '') {
    return { lbs: '', kgs: '', [source]: raw };
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return null;
  const w = v1WeightCascade(source, n);
  return {
    lbs: fmt(Number(w.lbs)),
    kgs: fmt(Number(w.kgs)),
    // The typed field keeps what was typed (see module doc).
    [source]: raw,
  };
}
